'use client';

import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  TransitionStartFunction,
  useContext,
  useEffect,
  useMemo,
  useState,
  useTransition
} from 'react';

type facetTypes = string | string[];

type equipmentProps = {
  model: string;
  serialNumber: string;
};
type Context = {
  isLoading: boolean;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
  selectedFacets: facetTypes;
  setSelectedFacets: Dispatch<SetStateAction<facetTypes>>;
  isPending: boolean;
  startTransition: Dispatch<TransitionStartFunction>;
  setFilterByEquipment: Dispatch<SetStateAction<boolean>>;
  filterByEquipment: boolean;
  equipment: equipmentProps;
  setEquipment: Dispatch<SetStateAction<equipmentProps>>;
};

type LoaderProps = {
  children: ReactNode;
  facets: facetTypes;
  filterByEquipment: boolean;
  model: string;
  serialNumber: string;
};

export const LoaderOverlayContext = createContext<Context>(null);

export const LoaderOverlayContextProvider = ({
  children,
  facets,
  filterByEquipment: defaultFilterByEquipment,
  model,
  serialNumber
}: LoaderProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [selectedFacets, setSelectedFacets] = useState(facets);
  const [filterByEquipment, setFilterByEquipment] = useState(
    defaultFilterByEquipment
  );
  const [equipment, setEquipment] = useState({
    model,
    serialNumber: serialNumber ?? ''
  });
  const [isPending, startTransition] = useTransition();

  useEffect(() => {
    setIsLoading(isPending);
  }, [isPending]);

  const contextValues = useMemo(
    () => ({
      isLoading,
      setIsLoading,
      selectedFacets,
      setSelectedFacets,
      isPending,
      startTransition,
      filterByEquipment,
      setFilterByEquipment,
      equipment,
      setEquipment
    }),
    [
      isLoading,
      setIsLoading,
      selectedFacets,
      setSelectedFacets,
      isPending,
      startTransition,
      filterByEquipment,
      setFilterByEquipment,
      equipment,
      setEquipment
    ]
  );

  return (
    <LoaderOverlayContext.Provider value={contextValues}>
      {children}
    </LoaderOverlayContext.Provider>
  );
};

export const useLoaderOverlay = () => useContext(LoaderOverlayContext);
